import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import FoodPreferencesService from "../sections/foodPreference/FoodPreferencesService";
import AllergyService from "../sections/allergies/AllergyService";
import readLatestSchedule from "../sections/dailySchedule/latestSchedule";
import { FP_V2_REFRESH } from "../sections/foodPreference/events";
import { ALG_V2_REFRESH } from "../sections/allergies/events";
import { DS_V2_REFRESH } from "../sections/dailySchedule/events";
import {
  SLEEP_QUALITY_OPTIONS,
  formatTimeAmPm,
  glassesToLiters,
} from "../sections/dailySchedule/data";
import "../styles/healthPlanProfilePanels.scss";

/**
 * Food preferences + Daily schedule in the health plan's left rail — the two
 * accordions legacy's health plan showed under its category list (new-bridge
 * HealthPlanItem.js), put back (Nittin, 2026-09-17).
 *
 * DISPLAY ONLY. Nothing here writes: both are edited in their own patient
 * sections, and each panel says so. The data is those sections' own v2 reads:
 *   · Food preferences — FoodPreferencesService.get (+ getConfig for the
 *     labels of the diet / non-veg / cuisine keys) and AllergyService.list
 *     for the Allergies row (legacy listed allergies in this panel too);
 *   · Daily schedule — the timeline, water intake and sleep quality of the
 *     patient's CURRENT day (latestSchedule: today if filled, else the newest
 *     filled day before it — the schedule is one document per day since
 *     ZY-10330). A day nobody filled answers `filled: false`; that is not the
 *     patient's schedule, so it is not shown as one.
 *
 * Collapsed by default, as legacy's were; a collapsed head carries the diet
 * type (legacy's subtitle) or the entry count. The reads re-run when a section
 * announces a save, and when this tab comes back into view, since the sections
 * live on another screen and are usually edited in another tab.
 *
 * Shared by phr-frontend and bridge-frontend byte-for-byte.
 */

// The options lists change with a deploy, not per patient — once a session.
let CONFIG_CACHE = null;

const asList = (v) => (Array.isArray(v) ? v : []);

const humanize = (key) => {
  const s = String(key || "").replace(/[-_]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

const labelsOf = (keys, options) =>
  asList(keys)
    .map((k) => {
      const hit = asList(options).find((o) => o && o.key === k);
      return (hit && hit.label) || humanize(k);
    })
    .filter(Boolean);

const chipNames = (chips) =>
  asList(chips)
    .map((c) => (c && typeof c === "object" ? c.name : c))
    .map((n) => String(n || "").trim())
    .filter(Boolean);

const errorText = (err, fallback) =>
  (err && typeof err.message === "string" && err.message) || (typeof err === "string" && err) || fallback;

const Chev = ({ open }) => (
  <svg
    className={`hpp-chev${open ? " is-open" : ""}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

Chev.propTypes = { open: PropTypes.bool };

const Panel = ({ id, title, summary, open, onToggle, readOnlyNote, children }) => (
  <section className={`hpp-panel${open ? " is-open" : ""}`} data-panel={id}>
    <button
      type="button"
      className="hpp-head"
      aria-expanded={open}
      aria-controls={`hpp-body-${id}`}
      onClick={onToggle}
    >
      <span className="hpp-head-text">
        <span className="hpp-title">{title}</span>
        {!open && summary ? <span className="hpp-summary">{summary}</span> : null}
      </span>
      <Chev open={open} />
    </button>
    {/* Always mounted, so it can animate; a closed body is visibility:hidden,
        which also takes it out of the tab order and the accessibility tree. */}
    <div className="hpp-collapse" id={`hpp-body-${id}`} role="region" aria-label={title}>
      <div className="hpp-clip">
        <div className="hpp-body">
          {children}
          <p className="hpp-readonly">
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            {readOnlyNote}
          </p>
        </div>
      </div>
    </div>
  </section>
);

Panel.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.string,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  readOnlyNote: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const Row = ({ label, children }) => (
  <div className="hpp-row">
    <dt className="hpp-label">{label}</dt>
    <dd className="hpp-value">{children}</dd>
  </div>
);

Row.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const Chips = ({ items, titles }) =>
  items.length === 0 ? (
    <span className="hpp-empty">—</span>
  ) : (
    <span className="hpp-chips">
      {items.map((name, i) => (
        <span key={`${name}-${i}`} className="hpp-chip" title={(titles && titles[i]) || undefined}>
          {name}
        </span>
      ))}
    </span>
  );

Chips.propTypes = { items: PropTypes.arrayOf(PropTypes.string).isRequired, titles: PropTypes.arrayOf(PropTypes.string) };

const Failed = ({ message, onRetry }) => (
  <div className="hpp-failed" role="alert">
    <span>{message}</span>
    <button type="button" className="hpp-retry" onClick={onRetry}>
      Retry
    </button>
  </div>
);

Failed.propTypes = { message: PropTypes.string.isRequired, onRetry: PropTypes.func.isRequired };

const HealthPlanProfilePanels = ({ patientId }) => {
  const [open, setOpen] = useState({ food: false, schedule: false });
  const [config, setConfig] = useState(CONFIG_CACHE);
  const [food, setFood] = useState({ status: "loading" });
  const [allergies, setAllergies] = useState({ status: "loading" });
  const [schedule, setSchedule] = useState({ status: "loading" });

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A response only lands if it answers the latest read for this patient.
  const seq = useRef({ food: 0, allergies: 0, schedule: 0 });
  const latest = useRef(patientId);
  latest.current = patientId;

  const read = useCallback(
    (kind, call, adopt, quiet) => {
      if (!patientId) return;
      const n = ++seq.current[kind];
      const setter = { food: setFood, allergies: setAllergies, schedule: setSchedule }[kind];
      const current = () => mounted.current && seq.current[kind] === n && latest.current === patientId;
      // A background refresh keeps what is on screen until the answer arrives.
      if (!quiet) setter({ status: "loading" });
      call(
        patientId,
        (res) => current() && setter({ status: "ready", data: adopt(res) }),
        (err) =>
          current() &&
          setter((prev) =>
            quiet && prev.status === "ready" ? prev : { status: "error", message: errorText(err, "Couldn't load this.") }
          )
      );
    },
    [patientId]
  );

  const loadFood = useCallback(
    (quiet) => {
      const svc = new FoodPreferencesService();
      read(
        "food",
        (pid, ok, ko) => svc.get(pid, ok, ko),
        (res) => {
          const dp = (res && res.dietaryPattern) || {};
          const fb = (res && res.foodBehavior) || {};
          return {
            dietTypes: asList(dp.dietTypes),
            nonVegSubOptions: asList(dp.nonVegSubOptions),
            // Cuisines are `{name, catalogId}` chips (federated from bridge).
            // Read-only display only needs the name; drop entries missing it
            // and pre-existing string entries from legacy docs.
            cuisinePreferences: chipNames(dp.cuisinePreferences),
            likes: chipNames(fb.likes),
            dislikes: chipNames(fb.dislikes),
            restrictions: chipNames(fb.restrictions),
            limitations: chipNames(fb.limitations),
            note: String(fb.note || "").trim(),
          };
        },
        quiet
      );
    },
    [read]
  );

  const loadAllergies = useCallback(
    (quiet) => {
      const svc = new AllergyService();
      read(
        "allergies",
        (pid, ok, ko) => svc.list(pid, ok, ko),
        (res) =>
          asList(res && res.allergies)
            .filter((a) => a && !a.deleted && String(a.name || "").trim())
            .map((a) => ({
              name: String(a.name).trim(),
              detail: [a.category, chipNames(a.reactions).join(", ")].filter(Boolean).join(" · "),
            })),
        quiet
      );
    },
    [read]
  );

  const loadSchedule = useCallback(
    (quiet) => {
      read(
        "schedule",
        readLatestSchedule,
        (res) => {
          const hydration = (res && res.hydration) || {};
          const sleep = (res && res.sleep) || {};
          return {
            saved: !!(res && res.filled),
            entries: asList(res && res.entries)
              .filter((e) => e && !e.deleted && String(e.activity || "").trim())
              .slice()
              .sort((a, b) => (a.timeMinutes || 0) - (b.timeMinutes || 0)),
            glasses: Number(hydration.glasses) || 0,
            targetL: Number(hydration.targetL) || 0,
            quality: sleep.quality || "",
          };
        },
        quiet
      );
    },
    [read]
  );

  const loadAll = useCallback(
    (quiet) => {
      loadFood(quiet);
      loadAllergies(quiet);
      loadSchedule(quiet);
    },
    [loadFood, loadAllergies, loadSchedule]
  );

  useEffect(() => {
    loadAll(false);
  }, [loadAll]);

  useEffect(() => {
    if (config) return;
    new FoodPreferencesService().getConfig(
      (res) => {
        CONFIG_CACHE = res || {};
        if (mounted.current) setConfig(CONFIG_CACHE);
      },
      // Without the options the raw keys are shown, humanised.
      () => {}
    );
  }, [config]);

  useEffect(() => {
    const onFood = () => loadFood(true);
    const onAllergies = () => loadAllergies(true);
    const onSchedule = () => loadSchedule(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") loadAll(true);
    };
    EventService.addListener(FP_V2_REFRESH, onFood);
    EventService.addListener(ALG_V2_REFRESH, onAllergies);
    EventService.addListener(DS_V2_REFRESH, onSchedule);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      EventService.removeListener(FP_V2_REFRESH, onFood);
      EventService.removeListener(ALG_V2_REFRESH, onAllergies);
      EventService.removeListener(DS_V2_REFRESH, onSchedule);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadFood, loadAllergies, loadSchedule, loadAll]);

  if (!patientId) return null;

  const cfg = config || {};
  const f = food.status === "ready" ? food.data : null;
  const dietLabels = f ? labelsOf(f.dietTypes, cfg.dietTypes) : [];
  const nonVegLabels = f ? labelsOf(f.nonVegSubOptions, cfg.nonVegOptions) : [];
  // Names extracted at load time (see loadFood above) — no key→label lookup.
  const cuisineLabels = f ? f.cuisinePreferences : [];

  const foodSummary =
    food.status === "loading"
      ? "Loading…"
      : food.status === "error"
      ? "Couldn't load"
      : dietLabels.length
      ? dietLabels.join(", ")
      : "Diet type not recorded";

  const s = schedule.status === "ready" ? schedule.data : null;
  const shown = s && s.saved ? s.entries : [];
  const scheduleSummary =
    schedule.status === "loading"
      ? "Loading…"
      : schedule.status === "error"
      ? "Couldn't load"
      : shown.length
      ? `${shown.length} ${shown.length === 1 ? "entry" : "entries"}`
      : "Not recorded yet";
  const quality = s && SLEEP_QUALITY_OPTIONS.find((o) => o.key === s.quality);

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div className="hpp">
      <Panel
        id="food"
        title="Food preferences"
        summary={foodSummary}
        open={open.food}
        onToggle={() => toggle("food")}
        readOnlyNote="View only · edit in Food preferences and Allergies"
      >
        {food.status === "loading" && <p className="hpp-loading">Loading…</p>}
        {food.status === "error" && <Failed message={food.message} onRetry={() => loadFood(false)} />}
        {f && (
          <dl className="hpp-list">
            <Row label="Diet type">
              <Chips items={dietLabels} />
            </Row>
            {nonVegLabels.length > 0 && (
              <Row label="Non-veg sub-options">
                <Chips items={nonVegLabels} />
              </Row>
            )}
            <Row label="Cuisine preferences">
              <Chips items={cuisineLabels} />
            </Row>
            <Row label="Likes">
              <Chips items={f.likes} />
            </Row>
            <Row label="Dislikes">
              <Chips items={f.dislikes} />
            </Row>
            <Row label="Restrictions">
              <Chips items={f.restrictions} />
            </Row>
            <Row label="Limitations">
              <Chips items={f.limitations} />
            </Row>
            <Row label="Allergies">
              {allergies.status === "loading" && <span className="hpp-empty">Loading…</span>}
              {allergies.status === "error" && (
                <Failed message={allergies.message} onRetry={() => loadAllergies(false)} />
              )}
              {allergies.status === "ready" && (
                <Chips items={allergies.data.map((a) => a.name)} titles={allergies.data.map((a) => a.detail)} />
              )}
            </Row>
            <Row label="Note">
              {f.note ? <span className="hpp-text">{f.note}</span> : <span className="hpp-empty">—</span>}
            </Row>
          </dl>
        )}
      </Panel>

      <Panel
        id="schedule"
        title="Daily schedule"
        summary={scheduleSummary}
        open={open.schedule}
        onToggle={() => toggle("schedule")}
        readOnlyNote="View only · edit in Daily schedule"
      >
        {schedule.status === "loading" && <p className="hpp-loading">Loading…</p>}
        {schedule.status === "error" && (
          <Failed message={schedule.message} onRetry={() => loadSchedule(false)} />
        )}
        {s && (
          <>
            {shown.length ? (
              <ol className="hpp-timeline">
                {shown.map((e, i) => (
                  <li key={e.entryId || `${e.timeMinutes}-${i}`} className="hpp-slot">
                    <span className="hpp-time">{formatTimeAmPm(e.timeMinutes)}</span>
                    <span className="hpp-activity">{e.activity}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="hpp-none">No schedule saved yet.</p>
            )}
            <dl className="hpp-list">
              <Row label="Water intake">
                {s.glasses > 0 ? (
                  <span className="hpp-text">
                    {s.glasses} {s.glasses === 1 ? "glass" : "glasses"} · {glassesToLiters(s.glasses).toFixed(1)} L
                  </span>
                ) : (
                  <span className="hpp-empty">—</span>
                )}
              </Row>
              <Row label="Water target">
                {s.targetL > 0 ? <span className="hpp-text">{s.targetL} L</span> : <span className="hpp-empty">—</span>}
              </Row>
              <Row label="Sleep quality">
                {quality ? (
                  <span className="hpp-quality" style={{ background: quality.bg, color: quality.fg }}>
                    {quality.label}
                  </span>
                ) : (
                  <span className="hpp-empty">—</span>
                )}
              </Row>
            </dl>
          </>
        )}
      </Panel>
    </div>
  );
};

HealthPlanProfilePanels.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default HealthPlanProfilePanels;
